import { hreflangTags, ogLocales } from '@/lib/locale';
import { ogImageSize } from '@/lib/open-graph';
import { type Locale, baseLocale, getLocale, locales, localizeHref } from '@/paraglide/runtime';
import { config } from '@/site-config';

/** The absolute URL of a site path in a locale, the current page's unless one is given. */
function siteUrl(path: string, locale?: Locale) {
  return new URL(localizeHref(path, locale && { locale }), config.siteUrl).href;
}

interface PageHeadOptions {
  title: string;
  description: string;
  /**
   * Site-relative path without a locale, e.g. `/news`. In the page's locale it becomes the canonical
   * link and the `og:url`, and in every locale an `hreflang` alternate.
   */
  canonical: string;
  /** Site-relative path to the page's Open Graph image, without a locale. */
  image?: string;
  /** Use the title as given, instead of suffixing it with the site name. */
  absoluteTitle?: boolean;
  type?: 'website' | 'article';
  /** Extra meta tags, merged after the defaults so they can override them. */
  meta?: { name?: string; property?: string; content: string }[];
  /** Extra link tags, e.g. a feed alternate. */
  links?: { rel: string; href: string; type?: string; title?: string; hrefLang?: string }[];
  /** Script tags, e.g. a JSON-LD payload as `{ type, children }`. */
  scripts?: { type?: string; src?: string; children?: string }[];
  /** The visible breadcrumb trail, ending with this page, given to search engines as a BreadcrumbList. */
  breadcrumbs?: { name: string; path: string }[];
  /**
   * The locales the page is written in, for content that may not be translated yet. A page shown in
   * another locale's chrome gives the English one as canonical, and only these get alternates.
   */
  translated?: readonly Locale[];
}

/** Per-route head tags. The title is suffixed with the site name unless the route opts out. */
export function pageHead({
  title,
  description,
  canonical,
  image,
  absoluteTitle = false,
  type = 'website',
  meta = [],
  links = [],
  scripts = [],
  breadcrumbs = [],
  translated = locales,
}: PageHeadOptions) {
  // The language the page's content is in, which is English for a page not translated yet.
  const locale = translated.includes(getLocale()) ? getLocale() : baseLocale;
  const url = siteUrl(canonical, locale);
  const resolvedTitle = absoluteTitle ? title : `${title} | ${config.title}`;

  return {
    meta: [
      { title: resolvedTitle },
      { name: 'description', content: description },
      { property: 'og:type', content: type },
      { property: 'og:title', content: resolvedTitle },
      { property: 'og:description', content: description },
      { property: 'og:url', content: url },
      { property: 'og:locale', content: ogLocales[locale] },
      ...translated
        .filter((other) => other !== locale)
        .map((other) => ({ property: 'og:locale:alternate', content: ogLocales[other] })),
      ...(image
        ? [
            // Under the content's locale prefix, so the build renders it in that language.
            { property: 'og:image', content: siteUrl(image, locale) },
            { property: 'og:image:type', content: 'image/webp' },
            // Facebook and LinkedIn lay the preview out from these before fetching the image.
            { property: 'og:image:width', content: String(ogImageSize.width) },
            { property: 'og:image:height', content: String(ogImageSize.height) },
            // X falls back to the og: tags for title, description and image, so this is all it needs.
            { name: 'twitter:card', content: 'summary_large_image' },
          ]
        : []),
      ...meta,
    ],
    links: [
      { rel: 'canonical', href: url },
      ...translated.map((other) => ({
        rel: 'alternate',
        hrefLang: hreflangTags[other],
        href: siteUrl(canonical, other),
      })),
      { rel: 'alternate', hrefLang: 'x-default', href: siteUrl(canonical, baseLocale) },
      ...links,
    ],
    scripts: breadcrumbs.length > 0 ? [...scripts, breadcrumbScript(breadcrumbs)] : scripts,
  };
}

function breadcrumbScript(breadcrumbs: NonNullable<PageHeadOptions['breadcrumbs']>) {
  return {
    type: 'application/ld+json',
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': crumb.name,
        'item': siteUrl(crumb.path),
      })),
    }),
  };
}
